import React from "react";
import { getVastuInfo, getZoneColor } from "@/lib/vastu";
import { evaluateMarker, STATUS_COLORS } from "@/lib/rooms";
import { sectorLabelAnchor, formatDeg } from "@/lib/sectors";

export function PolygonLayer({ polygon, scale }) {
  const dash = `${6 / scale} ${4 / scale}`;
  if (polygon.length === 2) {
    return (
      <line x1={polygon[0].x} y1={polygon[0].y} x2={polygon[1].x} y2={polygon[1].y}
        stroke="#F59E0B" strokeWidth={2 / scale} strokeDasharray={dash} pointerEvents="none" />
    );
  }
  if (polygon.length < 3) return null;
  return (
    <polygon points={polygon.map((p) => `${p.x},${p.y}`).join(" ")}
      fill="rgba(245, 158, 11, 0.06)" stroke="#F59E0B" strokeWidth={2 / scale} strokeDasharray={dash} pointerEvents="none" />
  );
}

export function SectorLayer({ sectors, center, sectorCount, hoveredSector, mode, scale, rotation = 0, showDegrees = true, onHoverSector, onClickSector }) {
  const labelSize = (sectorCount === 32 ? 10 : sectorCount === 16 ? 12 : 15) / scale;
  const tick = 8 / scale;
  const gap = (sectorCount === 32 ? 14 : sectorCount === 16 ? 18 : 26) / scale;
  const stack = sectorCount === 8; // code above, Sanskrit below
  return (
    <>
      {center && sectors[0]?.radius && (
        <g pointerEvents="none">
          <circle cx={center.x} cy={center.y} r={sectors[0].radius} fill="none"
            stroke="#F59E0B" strokeOpacity={0.5} strokeWidth={1.5 / scale} />
          {[0.4, 0.68].map((f) => (
            <circle key={f} cx={center.x} cy={center.y} r={sectors[0].radius * f} fill="none"
              stroke="#F59E0B" strokeOpacity={0.22} strokeWidth={1 / scale} />
          ))}
          {(() => {
            const R = sectors[0].radius;
            const lines = [];
            const tick = (pt, len, op, keyPrefix, key) => {
              const d = Math.hypot(pt.x - center.x, pt.y - center.y) || 1;
              const ux = (pt.x - center.x) / d, uy = (pt.y - center.y) / d;
              lines.push(
                <line key={`${keyPrefix}-${key}`} x1={center.x + ux * R} y1={center.y + uy * R}
                  x2={center.x + ux * (R + len)} y2={center.y + uy * (R + len)}
                  stroke="#F59E0B" strokeOpacity={op} strokeWidth={1 / scale} />
              );
            };
            sectors.forEach((s, i) => tick(s.vertices[1], R * 0.045, 0.7, "maj", i));
            if (sectors.length <= 16) {
              sectors.forEach((s, i) => s.vertices.slice(2, 9).forEach((v, j) => tick(v, R * 0.022, 0.4, "min", `${i}-${j}`)));
            }
            // Emphasised cardinal ticks (N / E / S / W) — longer & bolder for quick orientation
            [0, 90, 180, 270].forEach((ca, k) => {
              const a = ((ca + rotation) * Math.PI) / 180;
              const ux = Math.sin(a), uy = -Math.cos(a);
              lines.push(
                <line key={`card-${k}`} x1={center.x + ux * R} y1={center.y + uy * R}
                  x2={center.x + ux * (R + R * 0.095)} y2={center.y + uy * (R + R * 0.095)}
                  stroke="#F59E0B" strokeOpacity={1} strokeWidth={3 / scale} strokeLinecap="round" />
              );
            });
            return lines;
          })()}
        </g>
      )}
      {sectors.map((s) => {
        const zc = getZoneColor(s.code);
        const isHovered = hoveredSector === s.index;
        return (
          <polygon
            key={s.index}
            data-sector-idx={s.index}
            data-testid={`sector-wedge-${s.index}`}
            className="sector-wedge"
            points={s.vertices.map((v) => `${v.x},${v.y}`).join(" ")}
            fill={zc}
            fillOpacity={isHovered ? 0.6 : 0.28}
            stroke={zc}
            strokeOpacity={isHovered ? 1 : 0.6}
            strokeWidth={(isHovered ? 2.5 : 1) / scale}
            onMouseEnter={() => onHoverSector?.(s.index)}
            onMouseLeave={() => onHoverSector?.(null)}
            onClick={(e) => { e.stopPropagation(); onClickSector?.(s.index); }}
            style={{ cursor: mode === "draw" ? "crosshair" : "pointer", pointerEvents: mode === "pan" ? "auto" : "none" }}
          />
        );
      })}
      {center && sectors.map((s) => {
        const info = getVastuInfo(s.code);
        const zc = getZoneColor(s.code);
        const { boundary, anchor, ux, uy } = sectorLabelAnchor(s, center, tick + gap);
        const isHovered = hoveredSector === s.index;
        return (
          <g key={`lbl-${s.index}`} pointerEvents="none" data-testid={`sector-label-${s.index}`}>
            <line x1={boundary.x} y1={boundary.y} x2={boundary.x + ux * tick} y2={boundary.y + uy * tick}
              stroke={zc} strokeOpacity={0.9} strokeWidth={1.2 / scale} />
            <text x={anchor.x} y={anchor.y - (stack ? 6 / scale : 0)} textAnchor="middle" dominantBaseline="middle" fontSize={labelSize}
              fontFamily="Fira Code, monospace" fontWeight={isHovered ? "800" : "600"} fill={isHovered ? "#FDE68A" : "#F9FAFB"}
              style={{ paintOrder: "stroke", stroke: "#0B1120", strokeWidth: 3 / scale }}>
              {s.code}
            </text>
            {stack && (
              <text x={anchor.x} y={anchor.y + 8 / scale} textAnchor="middle" dominantBaseline="middle" fontSize={10 / scale}
                fontFamily="Outfit, sans-serif" fill={zc}
                style={{ paintOrder: "stroke", stroke: "#0B1120", strokeWidth: 3 / scale }}>
                {info.sanskrit}
              </text>
            )}
            <text x={anchor.x} y={anchor.y + (stack ? 20 / scale : labelSize * 0.95)} textAnchor="middle" dominantBaseline="middle"
              fontSize={labelSize * 0.55} fontFamily="Fira Code, monospace" fontWeight="600" fill="#F59E0B"
              style={{ paintOrder: "stroke", stroke: "#0B1120", strokeWidth: 3 / scale, display: showDegrees ? undefined : "none" }}>
              {formatDeg(s.centerDeg)}
            </text>
          </g>
        );
      })}
    </>
  );
}

export function BrahmasthanMarker({ center, scale }) {
  return (
    <g data-testid="brahmasthan-center-marker" pointerEvents="none">
      <circle cx={center.x} cy={center.y} r={18 / scale} fill="rgba(236, 72, 153, 0.18)" className="pulse-ring" />
      <circle cx={center.x} cy={center.y} r={7 / scale} fill="#EC4899" className="brahma-glow" />
      <circle cx={center.x} cy={center.y} r={2.5 / scale} fill="#F9FAFB" />
    </g>
  );
}

export function PolygonPoints({ polygon, scale, onStartDrag }) {
  return polygon.map((p, i) => (
    <g key={`pt-${i}`}>
      <circle cx={p.x} cy={p.y} r={9 / scale} fill="#F59E0B" stroke="#0B1120" strokeWidth={2 / scale}
        data-point-idx={i} data-testid={`polygon-point-${i}`} style={{ cursor: "grab" }}
        onMouseDown={(e) => { e.stopPropagation(); onStartDrag(i); }} />
      <text x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle" fontSize={9 / scale}
        fill="#0B1120" fontFamily="Fira Code" fontWeight="700" pointerEvents="none">{i + 1}</text>
    </g>
  ));
}

export function MarkerLayer({ markers, center, rotation, selectedMarker, scale, onStartDrag, onSelect }) {
  const r = 11 / scale;
  return markers.map((m) => {
    const ev = evaluateMarker(m, center, rotation);
    const color = STATUS_COLORS[ev.status];
    const isSel = selectedMarker === m.id;
    return (
      <g key={m.id} data-testid={`room-marker-${m.id}`} data-status={ev.status}>
        {isSel && <circle cx={m.x} cy={m.y} r={r * 1.9} fill="none" stroke={color} strokeWidth={1.5 / scale}
          strokeDasharray={`${3 / scale} ${3 / scale}`} pointerEvents="none" />}
        <circle cx={m.x} cy={m.y} r={r} fill={color} stroke="#0B1120" strokeWidth={2 / scale}
          data-marker-id={m.id} style={{ cursor: "grab" }}
          onMouseDown={(e) => { e.stopPropagation(); onStartDrag(m.id); }}
          onClick={(e) => { e.stopPropagation(); onSelect?.(m.id); }} />
        {ev.status === "conflict" && (
          <text x={m.x} y={m.y + 0.5 / scale} textAnchor="middle" dominantBaseline="middle" fontSize={13 / scale}
            fontWeight="800" fontFamily="Fira Code" fill="#0B1120" pointerEvents="none">!</text>
        )}
        <text x={m.x} y={m.y - r - 6 / scale} textAnchor="middle" fontSize={11 / scale} fontFamily="Outfit, sans-serif"
          fontWeight="600" fill="#F9FAFB" style={{ paintOrder: "stroke", stroke: "#0B1120", strokeWidth: 3 / scale }} pointerEvents="none">
          {ev.room.label}{ev.direction ? ` · ${ev.direction}` : ""}
        </text>
      </g>
    );
  });
}

// Faint detected-corner dots + highlighted snap target
export function SnapLayer({ corners, snap, scale }) {
  return (
    <g pointerEvents="none" data-testid="snap-layer">
      {corners.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={2.2 / scale} fill="#38BDF8" fillOpacity={0.35 + c.strength * 0.4} />
      ))}
      {snap?.kind === "corner" && (
        <g data-testid="snap-target">
          <circle cx={snap.point.x} cy={snap.point.y} r={12 / scale} fill="none" stroke="#38BDF8" strokeWidth={2 / scale} className="pulse-ring" />
          <circle cx={snap.point.x} cy={snap.point.y} r={3 / scale} fill="#38BDF8" />
        </g>
      )}
      {snap?.kind === "axis" && (
        <line data-testid="snap-axis-guide" x1={snap.target.x} y1={snap.target.y} x2={snap.point.x} y2={snap.point.y}
          stroke="#38BDF8" strokeWidth={1.5 / scale} strokeDasharray={`${4 / scale} ${4 / scale}`} />
      )}
    </g>
  );
}

export function NorthIndicator({ rotation }) {
  return (
    <g transform="translate(60, 60)" pointerEvents="none" data-testid="north-indicator">
      <circle r="34" fill="rgba(17, 24, 39, 0.85)" stroke="#1F2937" />
      <g transform={`rotate(${rotation})`} data-testid="north-arrow">
        <path d="M 0 -24 L 8 12 L 0 6 L -8 12 Z" fill="#F59E0B" />
        <path d="M 0 -24 L 8 12 L 0 6 Z" fill="#FBBF24" />
        <text y="-28" textAnchor="middle" fontSize="10" fontFamily="Fira Code" fill="#F59E0B" fontWeight="700">N</text>
      </g>
    </g>
  );
}
